<template>
  <v-card data-testid="eudr-shape-editor">
    <v-card-title class="primary white--text">
      <span class="headline text-truncate">{{ $t('eudrEditShapesTitle') }}</span>
      <v-spacer/>
      <Icon
        dataTestId="eudr-shape-editor-close"
        icon="mdi-close"
        iconColor="white"
        :tooltipText="$t('close')"
        :small="false"
        :large="true"
        @icon-clicked="$emit('close')"
      />
    </v-card-title>

    <v-card-text class="pa-0">
      <v-row no-gutters>
        <v-col cols="12" md="3">
          <div class="px-4 py-2 body-2">
            {{ $t(allShapesArePoints ? 'eudrPointsSummary' : 'eudrShapesSummary', { count: rows.length, flagged: flaggedCount }) }}
          </div>
          <v-divider/>
          <!-- Hidden for point-only files: there is nothing to simplify. -->
          <div v-if="hasPolygonShapes" class="px-4 py-2 d-flex align-center">
            <v-text-field
              v-model.number="reduceThreshold"
              type="number"
              dense
              hide-details
              class="eudr-reduce-threshold"
              :label="$t('eudrMaxVerticesLabel')"
            />
            <v-btn
              small
              outlined
              color="primary"
              class="ml-2"
              id="eudr-editor-reduce-all"
              :disabled="!reduceAllEnabled"
              @click="reduceAllVertices"
            >
              <v-icon small left>mdi-auto-fix</v-icon>
              {{ $t('eudrReduceAll') }}
            </v-btn>
            <Icon
              icon="mdi-information-outline"
              iconColor="info"
              margin="ml-1"
              tooltipClass="eudr-reduce-info-tooltip"
              :tooltipText="$t('eudrReduceVerticesInfo')"
            />
          </div>
          <v-divider v-if="hasPolygonShapes"/>
          <div class="eudr-shape-list" :class="{ 'eudr-shape-list--no-reduce': !hasPolygonShapes }">
            <div
              v-for="row in rows"
              :key="row.featureId"
              :ref="'row-' + row.featureId"
              class="eudr-shape-row d-flex align-center px-4 py-2"
              :class="{ 'eudr-shape-selected': row.featureId === selectedFeatureId }"
              @click="select(row.featureId)"
            >
              <div class="flex-grow-1">
                <div class="body-2">{{ row.name }}</div>
                <div v-if="row.caption" class="caption grey--text text--darken-1">{{ row.caption }}</div>
              </div>
              <Icon
                v-if="row.warnings.length"
                icon="mdi-alert-octagon-outline"
                :iconColor="row.blocking ? 'error' : 'warning'"
                :tooltipText="warningTooltip(row)"
              />
            </div>
          </div>
        </v-col>

        <v-col cols="12" md="9" style="position: relative;">
          <div :id="mapId" data-testid="eudr-shape-editor-map" style="width: 100%; height: 70vh;"></div>

          <!-- Edit-mode options, shown while a shape is loaded for editing -->
          <div v-if="selectedFeatureId" class="eudr-editor-toolbar">
            <v-card v-if="drawingAction" class="eudr-drawing-hint px-3 py-2 mb-1">
              <div class="caption">{{ $t(drawingHintKey) }}</div>
              <v-btn
                small
                block
                color="white"
                class="mt-2 grey--text text--darken-2"
                id="eudr-editor-cancel-drawing"
                @click="cancelDrawing"
              >
                <v-icon small left>mdi-close</v-icon>
                {{ $t('eudrCancelDrawing') }}
              </v-btn>
            </v-card>
            <template v-else-if="!selectedIsPoint">
              <v-btn
                v-for="option in editOptions"
                :key="option.id"
                small
                class="mb-1 d-block"
                :color="editOption === option.id ? 'primary' : 'white'"
                :class="editOption === option.id ? 'white--text' : 'grey--text text--darken-2'"
                @click="setEditOption(option.id)"
              >
                <v-icon small left>{{ option.icon }}</v-icon>
                {{ $t(option.label) }}
              </v-btn>
              <v-btn
                small
                color="white"
                class="mb-1 d-block primary--text"
                id="eudr-editor-cut-out"
                @click="startDrawing('cut')"
              >
                <v-icon small left>mdi-content-cut</v-icon>
                {{ $t('eudrCutOutArea') }}
              </v-btn>
              <v-btn
                small
                color="white"
                class="mb-1 d-block primary--text"
                id="eudr-editor-redraw"
                @click="startDrawing('redraw')"
              >
                <v-icon small left>mdi-vector-polygon</v-icon>
                {{ $t('eudrRedrawShape') }}
              </v-btn>
              <v-btn
                small
                color="white"
                class="mb-1 d-block primary--text"
                id="eudr-editor-reduce-vertices"
                @click="reduceVertices"
              >
                <v-icon small left>mdi-auto-fix</v-icon>
                {{ $t('eudrReduceVertices') }}
              </v-btn>
              <v-btn
                v-if="selectedHasHoles"
                small
                color="white"
                class="mb-1 d-block primary--text"
                id="eudr-editor-split-holes"
                @click="splitSelectedHoles"
              >
                <v-icon small left>mdi-chart-donut</v-icon>
                {{ $t('eudrSplitHoles') }}
              </v-btn>
              <v-btn
                v-if="selectedNeedsRepair"
                small
                color="white"
                class="mb-1 d-block primary--text"
                id="eudr-editor-fix-geometry"
                @click="repairSelectedGeometry"
              >
                <v-icon small left>mdi-band-aid</v-icon>
                {{ $t('eudrFixGeometry') }}
              </v-btn>
              <!-- Only while a usable shape remains — removing the last shape is the Delete button's job. -->
              <v-btn
                v-if="selectedUnusableCount > 0 && selectedUnusableCount < selectedPartCount"
                small
                color="white"
                class="mb-1 d-block error--text"
                id="eudr-editor-remove-unusable"
                @click="removeUnusableShapes"
              >
                <v-icon small left>mdi-vector-square-remove</v-icon>
                {{ $t('eudrRemoveUnusable', { count: selectedUnusableCount }) }}
              </v-btn>
              <v-btn
                v-if="selectedCanRevert"
                small
                color="white"
                class="mb-1 d-block primary--text"
                id="eudr-editor-revert-point"
                @click="revertSelectedToPoint"
              >
                <v-icon small left>mdi-undo-variant</v-icon>
                {{ $t('eudrRevertToPoint') }}
              </v-btn>
            </template>
            <!-- Points have no vertices to drag, so the location is typed here or dragged on the map. -->
            <v-card v-else class="eudr-point-form px-3 pt-3 pb-2 mb-1">
              <v-text-field
                v-model="pointForm.latitude"
                dense
                hide-details
                id="eudr-editor-point-latitude"
                :label="$t('latitude')"
                @keyup.enter="applyPointForm"
              />
              <v-text-field
                v-model="pointForm.longitude"
                dense
                hide-details
                class="mt-4"
                id="eudr-editor-point-longitude"
                :label="$t('longitude')"
                @keyup.enter="applyPointForm"
              />
              <v-btn
                small
                block
                color="primary"
                class="mt-2"
                id="eudr-editor-apply-point"
                :disabled="!pointFormValid"
                @click="applyPointForm"
              >
                <v-icon small left>mdi-crosshairs-gps</v-icon>
                {{ $t('eudrUpdateLocation') }}
              </v-btn>
              <div class="caption grey--text text--darken-1 mt-1">{{ $t('eudrPointEditHint') }}</div>
              <v-btn
                small
                block
                outlined
                color="primary"
                class="mt-2"
                id="eudr-editor-draw-shape"
                @click="startDrawing('drawShape')"
              >
                <v-icon small left>mdi-vector-polygon</v-icon>
                {{ $t('eudrDrawShape') }}
              </v-btn>
            </v-card>
            <v-btn
              v-if="!drawingAction"
              small
              color="error"
              class="mb-1 d-block white--text"
              id="eudr-editor-remove-shape"
              @click="deleteSelected"
            >
              <v-icon small left>mdi-delete</v-icon>
              {{ $t('eudrDeleteShape') }}
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions>
      <span class="caption grey--text text--darken-1 ml-2">
        {{ selectedRow ? selectedRow.name : $t('eudrEditShapesHint') }}
      </span>
      <v-spacer/>
      <v-btn text @click="$emit('close')">{{ $t('cancel') }}</v-btn>
      <v-btn
        color="primary"
        id="eudr-shape-editor-done"
        @click="done"
      >{{ $t('eudrApplyEdits') }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import * as atlas from 'azure-maps-control'
import * as atlasDrawing from 'azure-maps-drawing-tools'
import * as turf from '@turf/turf'
import { mapMutations } from 'vuex'
import { azureMapsApiKey, zoomToFeature, mapResizeTimeout, defaultCoordinates, disposeMap } from '@/utils/map/MapHelpers'
import { dragHandleStyles, PolygonStatus } from '@/utils/map/MapDrawingManagerHelpers'
import {
  analyzeFeature, BLOCKING_WARNINGS, GeometryWarning, simplifyRing, dedupeConsecutive, ringIsClosed,
  ringSelfIntersects, ringIsDegenerate, ringIsZeroArea, countFeatureVertices, countFeatureParts, reduceGeometryVertices, positionIsValid,
  unusablePartIndexes, roundGeometryCoordinates, ringIsUndersized
} from '@/utils/eudr/EudrGeometry.js'
import { MAX_FEATURE_VERTICES } from '@/utils/eudr/EudrExport.js'
import { splitPolygonHoles } from '@/utils/eudr/EudrHoleSplit.js'
import { findOverlaps, OVERLAP_WARNING } from '@/utils/eudr/EudrOverlap.js'
import { featureDisplayName, isSyntheticParcelId } from '@/utils/eudr/EudrFeatureName.js'
import { applyDrawnShapeToPointFeature, canRevertToPoint, revertFeatureToPoint, movePointFeature } from '@/utils/eudr/EudrPointResolve.js'
import { uniqueDialogId } from '@/utils/componentHelpers'
import cloneDeep from 'lodash/cloneDeep'

const POLYGON_FILTER = ['any', ['==', ['geometry-type'], 'Polygon'], ['==', ['geometry-type'], 'MultiPolygon']]
const EDITABLE_TYPES = ['Polygon', 'MultiPolygon']
const MIN_RING_POINTS = 4 // triangle + closure
const MIN_REDUCE_THRESHOLD = MIN_RING_POINTS // a budget below one triangle can't produce a shape
const DRAWING_HINT_KEYS = { cut: 'eudrDrawHintCut', redraw: 'eudrDrawHintRedraw', drawShape: 'eudrDrawHintShape' }

// Number('') is 0 and Number('  ') is 0 — an empty coordinate field must read as "no value", not the equator.
const parseCoordinate = (value) => {
  const text = String(value ?? '').trim()
  return text.length ? Number(text) : NaN
}

export default {
  name: 'EudrShapeEditor',

  components: {
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    // The working FeatureCollection. Edited on a deep copy; the result is emitted via 'apply'.
    featureCollection: {
      type: Object,
      required: true
    },
    // Deep-link: open with this feature already loaded for editing (e.g. double-clicked on the review map).
    initialFeatureId: {
      type: [String, Number],
      required: false,
      default: null
    }
  },

  data () {
    return {
      mapId: uniqueDialogId('eudr-shape-editor-map'),
      working: null,
      selectedFeatureId: null,
      selectedWasMultiPolygon: false,
      selectedHasHoles: false,
      selectedUnusableCount: 0,
      selectedPartCount: 0,
      selectedIsPoint: false,
      // Text mirrors of the selected point's coordinates; committed via applyPointForm, never live.
      pointForm: { latitude: '', longitude: '' },
      // 'cut' | 'redraw' | 'drawShape' while the drawing manager is in draw-polygon mode for the selection.
      drawingAction: null,
      editOption: 'dragVertices',
      // Default to the EU per-feature budget the finalize sanitizer enforces.
      reduceThreshold: MAX_FEATURE_VERTICES
    }
  },

  computed: {
    editOptions () {
      return [
        { id: 'dragVertices', label: 'dragVertices', icon: 'mdi-vector-point-select' },
        { id: 'eraseVertices', label: 'eraseVertices', icon: 'mdi-vector-square-remove' },
        { id: 'dragAndRotate', label: 'dragAndRotate', icon: 'mdi-cursor-move' }
      ]
    },

    drawingHintKey () {
      return DRAWING_HINT_KEYS[this.drawingAction] ?? 'eudrDrawHintShape'
    },

    selectedCanRevert () {
      const entry = this.selectedFeatureId ? this.featureByEditorId(this.selectedFeatureId) : null
      return !!entry && canRevertToPoint(entry.feature)
    },

    // Cached against `working` so the O(n²) intersection test doesn't rerun on every re-render.
    overlappingEditorIds () {
      const features = this.working?.features ?? []
      const { overlappingIndices } = findOverlaps(this.working)
      return new Set(overlappingIndices.map(index => this.editorIdFor(features[index])))
    },

    // Memoized so `rows` and `refreshBackdrop` share one O(n²) self-intersection analysis, not two.
    warningsByEditorId () {
      const map = new Map()
      const features = this.working?.features ?? []
      features.forEach(feature => map.set(this.editorIdFor(feature), analyzeFeature(feature)))
      return map
    },

    // Stable sort, so file order survives within each severity tier.
    rows () {
      const severity = row => (row.blocking ? 0 : row.warnings.length ? 1 : 2)
      return (this.working?.features ?? []).map((feature) => {
        const editorId = this.editorIdFor(feature)
        const warnings = [...(this.warningsByEditorId.get(editorId) ?? [])]
        if (this.overlappingEditorIds.has(editorId)) warnings.push(OVERLAP_WARNING)
        return {
          featureId: editorId,
          name: featureDisplayName(feature),
          caption: this.rowCaption(feature),
          warnings,
          blocking: warnings.some(code => BLOCKING_WARNINGS.includes(code)),
          editable: EDITABLE_TYPES.includes(feature.geometry?.type)
        }
      }).sort((a, b) => severity(a) - severity(b))
    },

    flaggedCount () {
      return this.rows.filter(r => r.warnings.length).length
    },

    hasPolygonShapes () {
      return (this.working?.features ?? []).some(f => EDITABLE_TYPES.includes(f.geometry?.type))
    },

    // A CSV upload is all bare points — the summary should say so instead of calling them shapes.
    allShapesArePoints () {
      const features = this.working?.features ?? []
      return features.length > 0 && features.every(f => f.geometry?.type === 'Point')
    },

    selectedRow () {
      return this.rows.find(r => r.featureId === this.selectedFeatureId)
    },

    // Self-intersections and degenerate rings are excluded: they need a human, not a deterministic repair.
    selectedNeedsRepair () {
      const warnings = this.selectedRow?.warnings ?? []
      return warnings.includes(GeometryWarning.DuplicatePoints) || warnings.includes(GeometryWarning.OpenRing)
    },

    reduceAllEnabled () {
      return Number.isFinite(this.reduceThreshold) && Math.floor(this.reduceThreshold) >= MIN_REDUCE_THRESHOLD
    },

    pointFormValid () {
      return positionIsValid(parseCoordinate(this.pointForm.longitude), parseCoordinate(this.pointForm.latitude))
    }
  },

  created () {
    // Features and properties are copied so edits stay local until Apply. Geometry objects are shared with the
    // caller and never mutated in place (write-back assigns a new one), which keeps the analysis memos in
    // EudrGeometry/EudrOverlap warm — the caller has usually just analyzed this same collection.
    this.working = {
      ...this.featureCollection,
      features: (this.featureCollection.features ?? []).map(feature => ({ ...feature, properties: { ...feature.properties } }))
    }
    // Deliberately non-reactive: Vue 2 deep-observes class instances, so declaring these in data() would walk the
    // entire map / drawing-manager object graph and every shape pushed into a data source.
    this.map = null
    this.drawingManager = null
    this.backdropSource = null
    this.hoverLabelSource = null
    this.pointDrag = null
    this.boundHandles = new WeakSet()
    // Uploaded features have no featureId until composite generation, hence a transient editor id, kept in a
    // non-reactive side table that is never serialized; array indexes would renumber after every deletion.
    this.editorIds = new WeakMap()
    this.editorIdSeq = 0
    this.drawingBaseIds = []
  },

  mounted () {
    this.$nextTick(() => this.initMap())
    document.addEventListener('keydown', this.onKeyDown)
  },

  beforeDestroy () {
    document.removeEventListener('keydown', this.onKeyDown)
    if (this.drawingAction) this.setAllowEscapeToClose(true)
    if (this.drawingManager) {
      this.drawingManager.dispose()
      this.drawingManager = null
    }
    if (this.map) {
      disposeMap(this.map)
      this.map = null
    }
  },

  methods: {
    ...mapMutations('dialog', ['setAllowEscapeToClose']),

    editorIdFor (feature) {
      if (feature.properties?.featureId) return feature.properties.featureId
      let id = this.editorIds.get(feature)
      if (!id) {
        id = `editor-${this.editorIdSeq++}`
        this.editorIds.set(feature, id)
      }
      return id
    },

    // Keyboard review: ↑/↓ select shapes, N jumps to the next flagged one, Delete removes the selection.
    onKeyDown (e) {
      const tag = e.target?.tagName
      if (['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(tag) || e.target?.isContentEditable) return
      if (e.ctrlKey || e.metaKey || e.altKey) return

      // While drawing, the drawing manager owns the keyboard (F/C/Esc); only Esc gets our cleanup too.
      if (this.drawingAction) {
        if (e.key === 'Escape') this.cancelDrawing()
        return
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        this.moveSelection(e.key === 'ArrowDown' ? 1 : -1)
      } else if (e.key === 'n' || e.key === 'N') {
        this.selectNextFlagged()
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && this.selectedFeatureId) {
        e.preventDefault()
        this.deleteSelected()
      }
    },

    moveSelection (delta) {
      const rows = this.rows
      if (!rows.length) return
      const index = rows.findIndex(r => r.featureId === this.selectedFeatureId)
      const next = index === -1
        ? (delta > 0 ? 0 : rows.length - 1)
        : Math.min(Math.max(index + delta, 0), rows.length - 1)
      if (rows[next].featureId !== this.selectedFeatureId) this.select(rows[next].featureId)
    },

    selectNextFlagged () {
      const rows = this.rows
      if (!rows.length) return
      const start = rows.findIndex(r => r.featureId === this.selectedFeatureId)
      for (let i = 1; i <= rows.length; i++) {
        const row = rows[(start + i + rows.length) % rows.length]
        if (!row.warnings.length) continue
        if (row.featureId !== this.selectedFeatureId) this.select(row.featureId)
        return
      }
    },

    // A synthetic point:<lon>,<lat> parcelId is an upload requirement, not a real parcel number.
    rowCaption (feature) {
      if (feature.geometry?.type === 'Point') {
        const [lon, lat] = feature.geometry.coordinates ?? []
        if (Number.isFinite(lon) && Number.isFinite(lat)) {
          return `${this.$t('eudrLatLong')}: ${lat.toFixed(6)}, ${lon.toFixed(6)}`
        }
      }
      const parts = []
      const parcelId = feature.properties?.parcelId
      if (parcelId && !isSyntheticParcelId(parcelId)) parts.push(`${this.$t('parcelNumber')}: ${parcelId}`)
      if (EDITABLE_TYPES.includes(feature.geometry?.type)) {
        parts.push(this.$t('eudrShapeGeometrySummary', {
          vertices: countFeatureVertices(feature),
          shapes: countFeatureParts(feature)
        }))
      }
      return parts.join(' · ')
    },

    featureByEditorId (editorId) {
      const features = this.working?.features ?? []
      const index = features.findIndex((f, i) => this.editorIdFor(f) === editorId)
      return index === -1 ? null : { feature: features[index], index }
    },

    // v-for refs are arrays, hence the [0].
    scrollToRow (editorId) {
      this.$nextTick(() => {
        const refs = this.$refs['row-' + editorId]
        const el = Array.isArray(refs) ? refs[0] : refs
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
      })
    },

    initMap () {
      this.map = new atlas.Map(this.mapId, {
        center: [defaultCoordinates.longitude, defaultCoordinates.latitude],
        zoom: 12,
        view: 'Auto',
        style: 'satellite_road_labels',
        authOptions: { authType: 'subscriptionKey', subscriptionKey: azureMapsApiKey() }
      })
      this.map.events.add('ready', this.onMapReady)
    },

    onMapReady () {
      if (!this.map) return
      this.backdropSource = new atlas.source.DataSource()
      this.map.sources.add(this.backdropSource)

      const blocking = ['boolean', ['get', 'editorBlocking'], false]
      const warned = ['boolean', ['get', 'editorWarned'], false]
      const fillLayer = new atlas.layer.PolygonLayer(this.backdropSource, null, {
        fillColor: ['case', blocking, '#E53935', warned, '#FFC107', '#1E90FF'],
        fillOpacity: 0.25,
        filter: POLYGON_FILTER
      })
      const lineLayer = new atlas.layer.LineLayer(this.backdropSource, null, {
        strokeColor: ['case', blocking, '#E53935', warned, '#FFC107', '#FFFFFF'],
        strokeWidth: 2,
        filter: POLYGON_FILTER
      })
      this.map.layers.add([fillLayer, lineLayer], 'labels')
      // Points render as flags; names on hover only, since always-on labels are noise at composite scale.
      const pointLayer = new atlas.layer.SymbolLayer(this.backdropSource, null, {
        iconOptions: {
          image: ['case', blocking, 'marker-red', warned, 'marker-yellow', 'marker-blue'],
          anchor: 'bottom',
          allowOverlap: true,
          ignorePlacement: true
        },
        filter: ['==', ['geometry-type'], 'Point']
      })
      this.map.layers.add(pointLayer)
      this.initPointHoverLabels(pointLayer)
      this.map.events.add('click', fillLayer, this.onBackdropClick)
      this.map.events.add('click', pointLayer, this.onBackdropClick)
      // Points live in the backdrop: the drawing manager only handles polygon vertices.
      this.map.events.add('mousedown', pointLayer, this.onPointMouseDown)
      this.map.events.add('mousemove', pointLayer, () => { this.map.getCanvasContainer().style.cursor = 'pointer' })
      this.map.events.add('mouseleave', pointLayer, () => { if (!this.pointDrag) this.resetCursor() })
      this.map.events.add('mousemove', fillLayer, () => { this.map.getCanvasContainer().style.cursor = 'pointer' })
      this.map.events.add('mouseleave', fillLayer, () => this.resetCursor())

      this.drawingManager = new atlasDrawing.drawing.DrawingManager(this.map, {
        mode: 'idle',
        interactionType: 'click',
        shapeDraggingEnabled: false,
        shapeRotationEnabled: false,
        ...dragHandleStyles.default
      })
      this.guardDrawingManagerEdit()
      this.styleDrawingLayers()
      this.map.events.add('drawingcomplete', this.drawingManager, this.onDrawingComplete)

      this.refreshBackdrop()
      if (this.working?.features?.length) {
        zoomToFeature(this.working, this.map, { type: 'jump', maxZoom: 16 })
      }
      setTimeout(() => { if (this.map) this.map.map.resize() }, mapResizeTimeout)
      if (this.initialFeatureId != null) this.select(this.initialFeatureId)
    },

    styleDrawingLayers () {
      const layers = Object.values(this.drawingManager.getLayers()).concat(Object.values(this.drawingManager.getPreviewLayers()))
      layers.forEach(layer => {
        layer.setOptions({
          strokeColor: PolygonStatus.Focused.stroke,
          strokeWidth: 2,
          fillColor: PolygonStatus.Focused.fill,
          fillOpacity: 0.25
        })
      })
    },

    // A selected Point stays in the backdrop, because the drawing manager never renders points.
    refreshBackdrop () {
      if (!this.backdropSource) return
      const entries = (this.working?.features ?? [])
        .map((feature) => ({ feature, editorId: this.editorIdFor(feature) }))
      const features = entries
        .filter(entry => this.selectedIsPoint || entry.editorId !== this.selectedFeatureId)
        .map(entry => {
          const warnings = this.warningsByEditorId.get(entry.editorId) ?? []
          // Shallow copies only: a point drag replaces geometry.coordinates on the source's copy, not on the feature.
          return {
            type: 'Feature',
            geometry: { ...entry.feature.geometry },
            properties: {
              ...entry.feature.properties,
              editorId: entry.editorId,
              editorWarned: warnings.length > 0 || this.overlappingEditorIds.has(entry.editorId),
              editorBlocking: warnings.some(code => BLOCKING_WARNINGS.includes(code))
            }
          }
        })
      // An unusable part draws as nothing (zero area, sub-pixel outline), so plant a visible flag at its
      // location — including for the selected feature, whose real shapes live in the drawing manager.
      for (const entry of entries) {
        const geometry = entry.feature.geometry
        for (const index of unusablePartIndexes(geometry)) {
          const ring = geometry.type === 'Polygon' ? geometry.coordinates[0] : geometry.coordinates[index]?.[0]
          const position = ring?.[0]
          if (!position) continue
          features.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [position[0], position[1]] },
            properties: {
              editorId: entry.editorId,
              editorBlocking: true,
              editorUnusableLabel: this.$t('eudrUnusableShapeFlag')
            }
          })
        }
      }
      this.backdropSource.setShapes({ type: 'FeatureCollection', features })
    },

    // Known azure-maps-drawing-tools defect: edit() can get a raw feature; re-resolve it to the live Shape.
    // edit() and promoteHandle() are also the only two places new vertex handles appear, hence the binding there.
    guardDrawingManagerEdit () {
      const editHelper = this.drawingManager.editHelper
      const originalEdit = editHelper.edit.bind(editHelper)
      editHelper.edit = (shape) => {
        if (shape && typeof shape.getCoordinates !== 'function') {
          const shapeId = shape.properties?._azureMapsShapeId
          shape = (shapeId && this.drawingManager.getSource().getShapeById(shapeId)) || null
          if (!shape) return
        }
        originalEdit(shape)
        this.bindPrimaryHandles()
      }
      const originalPromote = editHelper.promoteHandle.bind(editHelper)
      editHelper.promoteHandle = (...args) => {
        originalPromote(...args)
        this.bindPrimaryHandles()
      }
    },

    // Erase-vertex clicks arrive through the drawing manager's vertex handles.
    bindPrimaryHandles () {
      if (!this.map) return
      for (const handle of this.drawingManager?.editHelper?.primaryHandles ?? []) {
        if (this.boundHandles.has(handle)) continue
        this.boundHandles.add(handle)
        this.map.events.add('mousedown', handle, () => this.onPrimaryHandleClicked(handle))
      }
    },

    // Uses the same ±3px box the drawing tools' own hit test uses.
    clickHitsDrawingShapes (e) {
      if (!this.drawingManager || !e?.pixel) return false
      try {
        const southWest = this.map.pixelsToPositions([[e.pixel[0] - 3, e.pixel[1] + 3]])[0]
        const northEast = this.map.pixelsToPositions([[e.pixel[0] + 3, e.pixel[1] - 3]])[0]
        const bbox = new atlas.data.BoundingBox(southWest, northEast)
        const layers = Object.values(this.drawingManager.getLayers()).filter(l => l && typeof l.getId === 'function')
        return this.map.layers.getRenderedShapes(bbox, layers).length > 0
      } catch (err) {
        return false
      }
    },

    onBackdropClick (e) {
      const shape = e?.shapes?.[0]
      if (!shape || this.drawingAction) return
      // With stacked duplicates, re-selecting the backdrop twin would rebuild the drawing source mid-click.
      if (this.selectedFeatureId && this.clickHitsDrawingShapes(e)) return
      const props = typeof shape.getProperties === 'function' ? shape.getProperties() : shape.properties
      if (props?.editorId != null) this.select(props.editorId)
    },

    // A private one-feature source (mirrors EudrMap) so only the hovered flag shows its name.
    initPointHoverLabels (pointLayer) {
      this.hoverLabelSource = new atlas.source.DataSource()
      this.map.sources.add(this.hoverLabelSource)
      this.map.layers.add(new atlas.layer.SymbolLayer(this.hoverLabelSource, null, {
        iconOptions: { image: 'none' },
        textOptions: {
          textField: ['get', 'label'],
          color: '#FFFFFF',
          haloColor: '#000000',
          haloWidth: 1.5,
          size: 12,
          anchor: 'top',
          offset: [0, 0.4],
          allowOverlap: true,
          ignorePlacement: true
        }
      }))
      this.map.events.add('mousemove', pointLayer, this.onPointLabelHover)
      this.map.events.add('mouseleave', pointLayer, this.clearPointLabel)
    },

    onPointLabelHover (e) {
      const shape = e?.shapes?.[0]
      if (!shape || !this.hoverLabelSource) return
      const feature = typeof shape.toJson === 'function' ? shape.toJson() : shape
      if (feature?.geometry?.type !== 'Point') return
      this.hoverLabelSource.setShapes([{
        type: 'Feature',
        geometry: feature.geometry,
        properties: { label: feature.properties?.editorUnusableLabel ?? feature.properties?.productionPlace ?? '' }
      }])
    },

    clearPointLabel () {
      if (this.hoverLabelSource) this.hoverLabelSource.setShapes([])
    },

    select (editorId) {
      if (editorId === this.selectedFeatureId) return
      this.applyCurrent()

      const entry = this.featureByEditorId(editorId)
      if (!entry) return
      const geometry = entry.feature.geometry
      // Point resolution lives in the composite review, where coverage by other sources is known.
      if (geometry?.type === 'Point') {
        this.selectedFeatureId = editorId
        this.selectedIsPoint = true
        const [lon, lat] = geometry.coordinates ?? []
        this.pointForm = { latitude: String(lat ?? ''), longitude: String(lon ?? '') }
        this.refreshBackdrop()
        this.scrollToRow(editorId)
        this.zoomTo(entry.feature)
        return
      }
      if (!EDITABLE_TYPES.includes(geometry?.type)) {
        this.setSnackError(this.$t('eudrShapeNotEditable'))
        this.zoomTo(entry.feature)
        return
      }

      this.selectedFeatureId = editorId
      this.selectedWasMultiPolygon = geometry.type === 'MultiPolygon'
      this.scrollToRow(editorId)

      // The drawing manager only edits simple polygons, so split parts here and recombine in applyCurrent.
      const source = this.drawingManager.getSource()
      source.clear()
      const polygonCoords = this.selectedWasMultiPolygon ? geometry.coordinates : [geometry.coordinates]
      // Copied because vertex erasing splices the loaded rings in place, and the working geometry is shared with the caller.
      polygonCoords.forEach(coords => {
        source.add(new atlas.data.Feature(new atlas.data.Polygon(cloneDeep(coords)), {}))
      })
      this.selectedHasHoles = polygonCoords.some(coords => coords.length > 1)
      this.syncUnusableState(polygonCoords)

      this.refreshBackdrop()
      this.setEditOption(this.editOption, true)

      const shapes = source.getShapes()
      if (shapes.length === 1) {
        this.$nextTick(() => { this.drawingManager.edit(shapes[0]) })
      }
      this.zoomTo(entry.feature)
    },

    // Write the drawing source's geometry back onto the working feature and leave the edit session.
    applyCurrent () {
      if (!this.selectedFeatureId || !this.drawingManager) return
      if (this.drawingAction) this.cancelDrawing({ resumeEdit: false })
      const entry = this.featureByEditorId(this.selectedFeatureId)
      this.drawingManager.setOptions({ mode: 'idle' })

      if (entry) {
        const shapes = this.drawingManager.getSource().getShapes()
          .filter(s => EDITABLE_TYPES.includes(s.data.geometry.type))
        if (shapes.length > 0) {
          entry.feature.geometry = this.selectedWasMultiPolygon || shapes.length > 1
            ? { type: 'MultiPolygon', coordinates: shapes.map(s => s.data.geometry.type === 'MultiPolygon' ? s.data.geometry.coordinates[0] : s.data.geometry.coordinates) }
            : { type: 'Polygon', coordinates: shapes[0].data.geometry.coordinates }
        }
      }

      this.drawingManager.getSource().clear()
      this.selectedFeatureId = null
      this.selectedWasMultiPolygon = false
      this.selectedHasHoles = false
      this.selectedUnusableCount = 0
      this.selectedPartCount = 0
      this.selectedIsPoint = false
      this.refreshBackdrop()
    },

    // Tracks how many of the loaded parts are unusable, for the Remove Unusable button's visibility.
    syncUnusableState (polygonCoordsList) {
      this.selectedPartCount = polygonCoordsList.length
      this.selectedUnusableCount = unusablePartIndexes({ type: 'MultiPolygon', coordinates: polygonCoordsList }).length
    },

    // Drops the parts that draw as nothing but block the EU submission; never removes the last usable shape.
    removeUnusableShapes () {
      if (!this.selectedFeatureId || !this.drawingManager) return
      const source = this.drawingManager.getSource()
      const shapes = source.getShapes().filter(s => EDITABLE_TYPES.includes(s.data.geometry.type))

      // Judged as one multi-part set: micro-sliver detection only applies alongside sibling shapes.
      const allCoords = shapes.map(shape => shape.data.geometry.coordinates)
      const unusable = new Set(unusablePartIndexes({ type: 'MultiPolygon', coordinates: allCoords }))
      const usable = allCoords.filter((_, index) => !unusable.has(index))
      const removed = allCoords.length - usable.length
      if (!removed || usable.length === 0) return

      this.drawingManager.setOptions({ mode: 'idle' })
      source.clear()
      usable.forEach(coords => {
        source.add(new atlas.data.Feature(new atlas.data.Polygon(coords), {}))
      })
      this.syncUnusableState(usable)
      this.setEditOption(this.editOption, true)
      const rebuilt = source.getShapes()
      if (rebuilt.length === 1) {
        this.$nextTick(() => { this.drawingManager.edit(rebuilt[0]) })
      }

      // Write back now (mirroring repairSelectedGeometry) so the row's warning and the flag clear live.
      const entry = this.featureByEditorId(this.selectedFeatureId)
      if (entry) {
        entry.feature.geometry = this.selectedWasMultiPolygon || usable.length > 1
          ? { type: 'MultiPolygon', coordinates: usable }
          : { type: 'Polygon', coordinates: usable[0] }
      }
      this.refreshBackdrop()

      this.setSnack(this.$t('eudrUnusableRemoved', { count: removed }))
    },

    // The EU rejects interior rings; same clipping the finalize sanitizer would apply, but visible now.
    splitSelectedHoles () {
      if (!this.selectedFeatureId || !this.drawingManager) return
      const source = this.drawingManager.getSource()
      const shapes = source.getShapes().filter(s => EDITABLE_TYPES.includes(s.data.geometry.type))

      const replacement = []
      let failed = false
      for (const shape of shapes) {
        const coords = shape.data.geometry.coordinates
        if (coords.length > 1) {
          const pieces = splitPolygonHoles(coords)
          if (pieces) {
            replacement.push(...pieces)
            continue
          }
          failed = true
        }
        replacement.push(coords)
      }

      this.drawingManager.setOptions({ mode: 'idle' })
      source.clear()
      replacement.forEach(coords => {
        source.add(new atlas.data.Feature(new atlas.data.Polygon(coords), {}))
      })
      this.selectedHasHoles = replacement.some(coords => coords.length > 1)
      this.syncUnusableState(replacement)
      this.setEditOption(this.editOption, true)

      if (failed) this.setSnackError(this.$t('eudrHolesSplitFailed'))
      else this.setSnack(this.$t('eudrHolesSplitDone', { count: replacement.length }))
    },

    // The repairs finalize would apply silently, surfaced so the reviewer sees the corrected geometry now.
    repairSelectedGeometry () {
      if (!this.selectedFeatureId || !this.drawingManager) return
      const source = this.drawingManager.getSource()
      const shapes = source.getShapes().filter(s => EDITABLE_TYPES.includes(s.data.geometry.type))
      if (!shapes.length) return

      let closedRings = 0
      let removedPoints = 0
      const replacement = shapes.map(shape => shape.data.geometry.coordinates.map(ring => {
        // dedupeConsecutive also closes the ring; its `removed` counts true duplicates only.
        const { ring: fixed, removed } = dedupeConsecutive(ring)
        // Never make it worse: a ring the repair would collapse keeps its original coordinates.
        if (ringIsDegenerate(fixed)) return ring
        if (!ringIsClosed(ring)) closedRings++
        removedPoints += removed
        return fixed
      }))

      if (!closedRings && !removedPoints) {
        this.setSnack(this.$t('eudrGeometryRepairNone'))
        return
      }

      this.drawingManager.setOptions({ mode: 'idle' })
      source.clear()
      replacement.forEach(coords => {
        source.add(new atlas.data.Feature(new atlas.data.Polygon(coords), {}))
      })
      this.syncUnusableState(replacement)
      this.setEditOption(this.editOption, true)
      const rebuilt = source.getShapes()
      if (rebuilt.length === 1) {
        this.$nextTick(() => { this.drawingManager.edit(rebuilt[0]) })
      }

      // Write back now (mirroring applyCurrent's recombination) so the list row's warning clears live.
      const entry = this.featureByEditorId(this.selectedFeatureId)
      if (entry) {
        entry.feature.geometry = this.selectedWasMultiPolygon || replacement.length > 1
          ? { type: 'MultiPolygon', coordinates: replacement }
          : { type: 'Polygon', coordinates: replacement[0] }
      }

      this.setSnack(this.$t('eudrGeometryRepaired', { removed: removedPoints, closed: closedRings }))
    },

    deleteSelected () {
      if (!this.selectedFeatureId) return
      if (this.drawingAction) this.cancelDrawing({ resumeEdit: false })
      const entry = this.featureByEditorId(this.selectedFeatureId)
      // Selecting a Point never touches the drawing manager, so it can still be null on a keyboard Delete.
      if (this.drawingManager) {
        this.drawingManager.setOptions({ mode: 'idle' })
        this.drawingManager.getSource().clear()
      }
      if (entry) this.working.features.splice(entry.index, 1)
      this.selectedFeatureId = null
      this.selectedWasMultiPolygon = false
      this.selectedHasHoles = false
      this.selectedIsPoint = false
      this.refreshBackdrop()
      this.setSnack(this.$t('eudrShapeRemoved'))
    },

    applyPointForm () {
      if (!this.pointFormValid) return
      this.commitPointPosition(parseCoordinate(this.pointForm.longitude), parseCoordinate(this.pointForm.latitude), { zoom: true })
    },

    commitPointPosition (lon, lat, { zoom = false } = {}) {
      const entry = this.selectedIsPoint ? this.featureByEditorId(this.selectedFeatureId) : null
      if (!entry || entry.feature.geometry?.type !== 'Point') return
      if (!movePointFeature(entry.feature, lon, lat)) {
        this.refreshBackdrop() // a no-move drag may have nudged the backdrop shape sub-rounding
        return
      }
      const [longitude, latitude] = entry.feature.geometry.coordinates
      this.pointForm = { latitude: String(latitude), longitude: String(longitude) }
      this.refreshBackdrop()
      this.scrollToRow(this.selectedFeatureId) // fixing a flagged point re-sorts its row down the list
      if (zoom) this.zoomTo(entry.feature)
      this.setSnack(this.$t('eudrPointMoved'))
    },

    // Drag-to-move for the SELECTED point only; other points fall through to click-to-select.
    onPointMouseDown (e) {
      const shape = e?.shapes?.[0]
      if (!shape || !this.selectedIsPoint || this.drawingAction || typeof shape.setCoordinates !== 'function') return
      const props = typeof shape.getProperties === 'function' ? shape.getProperties() : shape.properties
      if (props?.editorId !== this.selectedFeatureId) return
      this.pointDrag = { shape, position: null }
      this.clearPointLabel()
      this.map.setUserInteraction({ dragPanInteraction: false })
      this.map.events.add('mousemove', this.onPointDragMove)
      this.map.events.add('mouseup', this.onPointDragEnd)
      this.map.events.add('mouseout', this.onPointDragEnd)
    },

    onPointDragMove (e) {
      if (!this.pointDrag || !e?.position) return
      this.pointDrag.position = e.position
      this.pointDrag.shape.setCoordinates(e.position)
    },

    onPointDragEnd () {
      const drag = this.pointDrag
      if (!drag) return
      this.pointDrag = null
      this.map.events.remove('mousemove', this.onPointDragMove)
      this.map.events.remove('mouseup', this.onPointDragEnd)
      this.map.events.remove('mouseout', this.onPointDragEnd)
      this.map.setUserInteraction({ dragPanInteraction: true })
      if (!drag.position) return // never moved: a plain re-click on the selected point
      // Panning across the antimeridian can hand back a longitude beyond ±180 — wrap it back into range.
      const lon = ((drag.position[0] + 540) % 360) - 180
      const lat = Math.min(Math.max(drag.position[1], -90), 90)
      this.commitPointPosition(lon, lat)
    },

    setEditOption (option, force = false) {
      if (!force && option === this.editOption) return
      this.editOption = option
      const focused = this.drawingManager.editHelper?.shape
      let options = {
        mode: 'edit-geometry',
        shapeDraggingEnabled: false,
        shapeRotationEnabled: false,
        ...dragHandleStyles.default
      }
      if (option === 'eraseVertices') options = { ...options, ...dragHandleStyles.deletion }
      if (option === 'dragAndRotate') {
        options.shapeDraggingEnabled = true
        options.shapeRotationEnabled = true
        options = { ...options, ...dragHandleStyles.none }
      }
      this.drawingManager.setOptions(options)
      if (focused) this.drawingManager.edit(focused)
    },

    onPrimaryHandleClicked (handle) {
      if (this.editOption !== 'eraseVertices') return
      const shape = this.drawingManager.editHelper?.shape
      if (!shape) return
      const position = handle.options.position
      const rings = shape.data.geometry.coordinates
      for (const ring of rings) {
        const index = ring.findIndex(coord => coord[0] === position[0] && coord[1] === position[1])
        if (index === -1) continue
        if (ring.length <= MIN_RING_POINTS) return // never erase below a triangle
        const removingAnchor = index === 0 || index === ring.length - 1
        for (let i = ring.length - 1; i >= 0; i--) {
          if (ring[i][0] === position[0] && ring[i][1] === position[1]) ring.splice(i, 1)
        }
        if (removingAnchor) ring.push([...ring[0]])
        break
      }
      this.$nextTick(() => { this.drawingManager.edit(shape) })
    },

    startDrawing (action) {
      if (!this.selectedFeatureId || !this.drawingManager || this.drawingAction) return
      if (this.selectedIsPoint !== (action === 'drawShape')) return
      this.drawingBaseIds = this.drawingManager.getSource().getShapes().map(s => s.getId())
      // Leaving edit mode fires drawingcomplete for the edited shape; arm drawingAction only after that.
      this.drawingManager.setOptions({ mode: 'draw-polygon' })
      this.drawingAction = action
      this.setAllowEscapeToClose(false)
    },

    // The selection's parts as they were before drawing began; the drawn polygon is the newcomer.
    baseShapes () {
      const ids = new Set(this.drawingBaseIds)
      return this.drawingManager.getSource().getShapes()
        .filter(s => ids.has(s.getId()) && EDITABLE_TYPES.includes(s.data.geometry.type))
    },

    onDrawingComplete (shape) {
      const action = this.drawingAction
      if (!action || !shape) return
      const parts = this.baseShapes().map(s => s.data.geometry.coordinates)
      const drawn = shape.data?.geometry?.type === 'Polygon' ? roundGeometryCoordinates(cloneDeep(shape.data.geometry.coordinates)) : null
      const outer = drawn ? dedupeConsecutive(drawn[0]).ring : null
      if (!outer || ringIsDegenerate(outer) || ringIsZeroArea(outer) || ringSelfIntersects(outer)) {
        this.finishDrawing()
        this.setSnackError(this.$t('eudrDrawnShapeInvalid'))
        return
      }

      let replacement
      let messageKey
      if (action === 'cut') {
        const cut = this.cutParts(parts, [outer])
        if (cut.error) {
          this.finishDrawing()
          this.setSnackError(this.$t(cut.error))
          return
        }
        replacement = cut.parts
        messageKey = replacement.some(coords => coords.length > 1) ? 'eudrAreaCutOutHoles' : 'eudrAreaCutOut'
      } else {
        replacement = [[outer]]
        messageKey = action === 'drawShape' ? 'eudrShapeDrawn' : 'eudrShapeRedrawn'
      }

      const geometry = replacement.length > 1 || (action === 'cut' && this.selectedWasMultiPolygon)
        ? { type: 'MultiPolygon', coordinates: replacement }
        : { type: 'Polygon', coordinates: replacement[0] }
      const entry = this.featureByEditorId(this.selectedFeatureId)
      if (entry) {
        if (action === 'drawShape') applyDrawnShapeToPointFeature(entry.feature, geometry)
        else entry.feature.geometry = geometry
      }
      this.selectedIsPoint = false
      this.selectedWasMultiPolygon = geometry.type === 'MultiPolygon'
      this.finishDrawing(replacement)
      this.refreshBackdrop()
      this.scrollToRow(this.selectedFeatureId)
      this.setSnack(this.$t(messageKey, { count: replacement.length }))
    },

    // turf.difference per part: a part the outline swallows disappears, a part it crosses may become several.
    cutParts (parts, clipCoords) {
      try {
        const clip = turf.polygon(clipCoords)
        const kept = []
        let changed = false
        for (const part of parts) {
          const subject = turf.polygon(part)
          const result = turf.difference(turf.featureCollection([subject, clip]))
          if (!result) {
            changed = true
            continue
          }
          if (Math.abs(turf.area(result) - turf.area(subject)) < 0.01) {
            kept.push(part)
            continue
          }
          changed = true
          const pieces = result.geometry.type === 'MultiPolygon' ? result.geometry.coordinates : [result.geometry.coordinates]
          for (const piece of pieces) {
            const rounded = roundGeometryCoordinates(piece).map(ring => dedupeConsecutive(ring).ring)
            if (!ringIsUndersized(rounded[0])) kept.push(rounded)
          }
        }
        if (!changed) return { error: 'eudrCutOutNoChange' }
        if (!kept.length) return { error: 'eudrCutOutRemovesAll' }
        return { parts: kept }
      } catch (e) {
        return { error: 'eudrCutOutFailed' }
      }
    },

    cancelDrawing ({ resumeEdit = true } = {}) {
      if (!this.drawingAction) return
      this.finishDrawing(null, { resumeEdit })
    },

    // Leaves draw-polygon mode. With `replacement`, the selection's parts are rebuilt from those coordinates;
    // without it, whatever the drawing added (a finished or half-drawn polygon) is dropped.
    finishDrawing (replacement = null, { resumeEdit = true } = {}) {
      const baseIds = new Set(this.drawingBaseIds)
      this.drawingAction = null
      this.drawingBaseIds = []
      this.setAllowEscapeToClose(true)
      const source = this.drawingManager.getSource()
      this.drawingManager.setOptions({ mode: 'idle' })
      if (replacement) {
        source.clear()
        replacement.forEach(coords => {
          source.add(new atlas.data.Feature(new atlas.data.Polygon(coords), {}))
        })
        this.selectedHasHoles = replacement.some(coords => coords.length > 1)
        this.syncUnusableState(replacement)
      } else {
        source.getShapes().filter(s => !baseIds.has(s.getId())).forEach(s => source.remove(s))
      }
      if (!resumeEdit || this.selectedIsPoint) return
      this.setEditOption(this.editOption, true)
      const shapes = source.getShapes()
      if (shapes.length === 1) {
        this.$nextTick(() => { this.drawingManager.edit(shapes[0]) })
      }
    },

    // Undo a parcel fetch / drawn area: the shape becomes the original point again and stays selected.
    revertSelectedToPoint () {
      const editorId = this.selectedFeatureId
      const entry = editorId ? this.featureByEditorId(editorId) : null
      if (!entry || !canRevertToPoint(entry.feature)) return
      this.applyCurrent()
      revertFeatureToPoint(entry.feature)
      this.select(editorId)
      this.setSnack(this.$t('eudrRevertedToPoint'))
    },

    resetCursor () {
      if (this.map) this.map.getCanvasContainer().style.cursor = 'grab'
    },

    // Escalate the RDP tolerance until the vertex count actually drops, so one click is one visible change.
    reduceVertices () {
      if (!this.selectedFeatureId || !this.drawingManager) return
      const shapes = this.drawingManager.getSource().getShapes()
        .filter(s => EDITABLE_TYPES.includes(s.data.geometry.type))
      if (!shapes.length) return

      const countShape = (coords) => coords.reduce((sum, ring) => sum + ring.length, 0)
      const before = shapes.reduce((sum, s) => sum + countShape(s.data.geometry.coordinates), 0)

      let epsilon = 0.000005 // ~0.5m, doubled per pass
      for (let pass = 0; pass < 12; pass++, epsilon *= 2) {
        const candidates = shapes.map(s => s.data.geometry.coordinates.map(ring => {
          const simplified = dedupeConsecutive(simplifyRing(ring, epsilon)).ring
          return ringIsDegenerate(simplified) || ringSelfIntersects(simplified) ? ring : simplified
        }))
        const after = candidates.reduce((sum, coords) => sum + countShape(coords), 0)
        if (after < before) {
          shapes.forEach((s, i) => s.setCoordinates(candidates[i]))
          const focused = this.drawingManager.editHelper?.shape
          if (focused) this.$nextTick(() => { this.drawingManager.edit(focused) })
          this.setSnack(this.$t('eudrVerticesReduced', { before, after }))
          return
        }
      }
      this.setSnack(this.$t('eudrVerticesNotReducible'))
    },

    // Commits any open edit session first, so in-progress geometry is included.
    reduceAllVertices () {
      if (!this.reduceAllEnabled) return
      const threshold = Math.floor(this.reduceThreshold)
      this.applyCurrent()

      let before = 0
      let after = 0
      let reducedCount = 0
      for (const feature of this.working?.features ?? []) {
        if (!EDITABLE_TYPES.includes(feature.geometry?.type)) continue
        const count = countFeatureVertices(feature)
        if (count <= threshold) continue
        const reduced = reduceGeometryVertices(feature.geometry, threshold)
        const newVertexCount = countFeatureVertices({ geometry: reduced })
        if (newVertexCount >= count) continue
        feature.geometry = reduced
        before += count
        after += newVertexCount
        reducedCount++
      }

      if (reducedCount === 0) {
        this.setSnack(this.$t('eudrReduceAllNone', { threshold }))
        return
      }
      this.refreshBackdrop()
      this.setSnack(this.$t('eudrReduceAllDone', { count: reducedCount, before, after }))
    },

    zoomTo (feature) {
      if (this.map) zoomToFeature(feature, this.map, { maxZoom: 17 })
    },

    warningTooltip (row) {
      const labels = row.warnings
        .map(code => this.$t(`eudrGeometry${code.charAt(0).toUpperCase()}${code.slice(1)}`))
      return `${this.$t('eudrGeometryWarningBadge')}: ${labels.join(', ')}`
    },

    done () {
      this.applyCurrent()
      this.$emit('apply', this.working)
    }
  }
}
</script>

<style scoped src="@/styles/map-scoped.css"/>
<style src="@/styles/map-global.css"/>
<style src="@/styles/atlas-drawing.css"/>
<style>
/* Unscopable (v-tooltip detaches its content); the cap stops one enormous line. */
.eudr-reduce-info-tooltip {
  display: inline-block;
  max-width: 340px;
}
</style>
<style scoped>
.eudr-shape-list {
  height: calc(70vh - 98px); /* summary row + reduce-all row */
  overflow-y: auto;
}
.eudr-shape-list--no-reduce {
  height: calc(70vh - 41px); /* summary row only (reduce-all hidden for point-only files) */
}
.eudr-reduce-threshold {
  max-width: 96px;
}
.eudr-shape-row {
  cursor: pointer;
}
.eudr-shape-row:hover {
  background: rgba(0, 0, 0, 0.04);
}
.eudr-shape-selected {
  background: rgba(209, 95, 39, 0.12);
}
.eudr-point-form {
  width: 200px;
}
.eudr-drawing-hint {
  width: 220px;
}
.eudr-editor-toolbar {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
</style>
